import { Card, CardContent } from '@shadcn/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@shadcn/components/ui/table';
import { Button } from "@shadcn/components/ui/button";
import { Download } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { useState } from 'react';

const BudgetForecastTable = ({ 
  loading, 
  futureForecastData, 
  selectedRegion,
  futureForecastChartData,
  futureForecastChartOptions 
}) => {
  const [isExporting, setIsExporting] = useState(false);

  // Export functionality
  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (!futureForecastData) {
        alert('No data to export');
        return;
      }

      // Prepare main forecast data
      const exportData = [];
      const months = Object.keys(futureForecastData.months || {});
      const categories = Object.keys(futureForecastData.categories || {})
        .filter(category => {
          // Filter out categories with all zero values
          const hasNonZeroValues = Object.values(futureForecastData.categories[category] || {})
            .some(value => value > 0);
          return hasNonZeroValues;
        });

      // Add monthly forecast data
      months.forEach(month => {
        const monthData = futureForecastData.months[month];
        const row = {
          'Month': month,
          'Growth Rate': monthData.growthRate !== undefined ? 
            `${(monthData.growthRate * 100).toFixed(1)}%` : '',
          'Total Units': monthData.total
        };

        // Add category data
        categories.forEach(category => {
          row[category] = monthData[category] || futureForecastData.categories[category][month] || 0;
        });

        exportData.push(row);
      });

      // Add current stock row if available
      if (futureForecastData.currentStock) {
        const currentStockRow = {
          'Month': 'Current Stock',
          'Growth Rate': '',
          'Total Units': futureForecastData.currentStock.total
        };

        categories.forEach(category => {
          currentStockRow[category] = futureForecastData.currentStock[category] || 0;
        });

        exportData.push(currentStockRow);
      }

      // Add totals row
      const totalsRow = {
        'Month': 'TOTAL',
        'Growth Rate': '',
        'Total Units': Object.values(futureForecastData.totalByMonth || {}).reduce((sum, val) => sum + val, 0)
      };

      categories.forEach(category => {
        const categoryTotal = Object.values(futureForecastData.categories[category] || {}).reduce((sum, val) => sum + val, 0);
        totalsRow[category] = categoryTotal;
      });

      exportData.push(totalsRow);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      
      // Create main data sheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Set column widths
      const columnWidths = [
        { wch: 15 }, // Month
        { wch: 12 }, // Growth Rate
        { wch: 12 }, // Total Units
        ...categories.map(() => ({ wch: 12 })) // Categories
      ];
      
      worksheet['!cols'] = columnWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Future Budget Forecast');
      
      // Add summary sheet
      const summaryData = [
        { 'Metric': 'Region', 'Value': futureForecastData.region || selectedRegion },
        { 'Metric': 'Export Date', 'Value': new Date().toLocaleDateString() },
        { 'Metric': 'Number of Months', 'Value': months.length },
        { 'Metric': 'Categories Included', 'Value': categories.length },
        { 'Metric': '', 'Value': '' },
        { 'Metric': 'Category Breakdown:', 'Value': '' },
        ...categories.map(category => ({
          'Metric': `  ${category}`,
          'Value': Object.values(futureForecastData.categories[category] || {}).reduce((sum, val) => sum + val, 0).toLocaleString()
        }))
      ];
      
      const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
      summaryWorksheet['!cols'] = [
        { wch: 20 }, // Metric
        { wch: 15 }, // Value
      ];
      
      XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');
      
      // Add notes sheet
      const notesData = [
        { 'Notes': 'Future Budget Forecast Export' },
        { 'Notes': '' },
        { 'Notes': `Region: ${futureForecastData.region || selectedRegion}` },
        { 'Notes': `Export Date: ${new Date().toLocaleDateString()}` },
        { 'Notes': '' },
        { 'Notes': 'Description:' },
        { 'Notes': 'This table shows the predicted budget forecast for each month and category based on current growth rates.' },
        { 'Notes': 'Current stock is shown for comparison.' },
        { 'Notes': '' },
        { 'Notes': 'Data includes:' },
        { 'Notes': '• Monthly forecasts by category' },
        { 'Notes': '• Growth rates applied' },
        { 'Notes': '• Current stock levels' },
        { 'Notes': '• Total projections' },
      ];
      
      const notesWorksheet = XLSX.utils.json_to_sheet(notesData);
      notesWorksheet['!cols'] = [{ wch: 60 }];
      
      XLSX.utils.book_append_sheet(workbook, notesWorksheet, 'Notes');
      
      // Generate filename
      const dateStr = new Date().toISOString().split('T')[0];
      const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM format
      const filename = `future-budget-forecast_${selectedRegion}_${currentMonth}_${dateStr}.xlsx`;
      
      // Export the file
      XLSX.writeFile(workbook, filename);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-wrap justify-between items-center mb-4">
            <h4 className="text-xl font-semibold">Future Budget Forecast for {selectedRegion}</h4>
            <Button variant="outline" size="sm" disabled>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
          <div className="flex justify-center items-center h-40">
            <div className="flex flex-col items-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
              <span className="mt-2 text-sm text-gray-500">Loading data...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!futureForecastData) {
    return (
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-wrap justify-between items-center mb-4">
            <h4 className="text-xl font-semibold">Future Budget Forecast for {selectedRegion}</h4>
            <Button variant="outline" size="sm" disabled>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
          <div className="text-center text-gray-500 py-6">
            No future forecast data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex flex-wrap justify-between items-center mb-4">
          <h4 className="text-xl font-semibold">Future Budget Forecast for {selectedRegion}</h4>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
        
        {/* Chart section */}
        {futureForecastChartData && (
          <div className="mb-6">
            <div className="h-[300px] md:h-[400px] w-full">
              <Bar data={futureForecastChartData} options={futureForecastChartOptions} />
            </div>
          </div>
        )}
        
        {/* Table section */}
        <div className="overflow-x-auto overflow-y-auto max-h-[600px] pb-4 shadow border rounded-lg">
          <div className="text-center mb-2 text-xs text-gray-500 lg:hidden">
            ← Scroll horizontally to view all columns →
          </div>
          
          <Table className="min-w-max w-full text-sm">
            <TableHeader className="sticky top-0 z-10">
              <TableRow className="bg-gray-50 sticky top-0">
                                 <TableHead className="font-semibold sticky top-0 bg-gray-50 border-b border-gray-200" style={{ minWidth: '100px', maxWidth: '120px' }}>
                   Month {futureForecastData.region || selectedRegion}
                 </TableHead>
                 <TableHead className="text-right font-semibold sticky top-0 bg-gray-50 border-b border-gray-200" style={{ minWidth: '80px', maxWidth: '100px' }}>
                   Growth Rate
                 </TableHead>
                 <TableHead className="text-right font-semibold sticky top-0 bg-gray-50 border-b border-gray-200" style={{ minWidth: '80px', maxWidth: '100px' }}>
                   Total Units
                 </TableHead>
                {Object.keys(futureForecastData.categories || {})
                  .filter(category => {
                    // Filter out categories with all zero values
                    const hasNonZeroValues = Object.values(futureForecastData.categories[category] || {})
                      .some(value => value > 0);
                    return hasNonZeroValues;
                  })
                                     .map(category => (
                     <TableHead key={category} className="text-right font-semibold sticky top-0 bg-gray-50 border-b border-gray-200" style={{ minWidth: '80px', maxWidth: '100px' }}>
                       <div className="truncate" title={category}>
                         {category}
                       </div>
                     </TableHead>
                   ))}
               </TableRow>
            </TableHeader>
            <TableBody>
              {Object.keys(futureForecastData.months || {}).map((month, index) => {
                const monthData = futureForecastData.months[month];
                return (
                  <TableRow key={month} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <TableCell className="font-medium">{month}</TableCell>
                    <TableCell className="text-right">
                      {monthData.growthRate !== undefined ? (
                        <span className={monthData.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {(monthData.growthRate * 100).toFixed(0)}%
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {monthData.total.toLocaleString()}
                    </TableCell>
                    {Object.keys(futureForecastData.categories || {})
                      .filter(category => {
                        // Filter out categories with all zero values
                        const hasNonZeroValues = Object.values(futureForecastData.categories[category] || {})
                          .some(value => value > 0);
                        return hasNonZeroValues;
                      })
                      .map(category => (
                        <TableCell key={`${month}-${category}`} className="text-right">
                          {(monthData[category] || futureForecastData.categories[category][month] || 0).toLocaleString()}
                        </TableCell>
                      ))}
                  </TableRow>
                );
              })}
              
              {/* Add Current Stock Row */}
              {futureForecastData.currentStock && (
                <TableRow className="bg-blue-50 border-t-2 border-blue-200">
                  <TableCell className="font-bold">Current Stock</TableCell>
                  <TableCell className="text-right">-</TableCell>
                  <TableCell className="text-right font-bold">
                    {futureForecastData.currentStock.total.toLocaleString()}
                  </TableCell>
                  {Object.keys(futureForecastData.categories || {})
                    .filter(category => {
                      // Filter out categories with all zero values
                      const hasNonZeroValues = Object.values(futureForecastData.categories[category] || {})
                        .some(value => value > 0);
                      return hasNonZeroValues;
                    })
                    .map(category => (
                      <TableCell key={`current-${category}`} className="text-right font-bold">
                        <span className={futureForecastData.currentStock[category] < 0 ? 'text-red-600' : 'text-green-600'}>
                          {(futureForecastData.currentStock[category] || 0).toLocaleString()}
                        </span>
                      </TableCell>
                    ))}
                </TableRow>
              )}
            </TableBody>
            
            {/* Footer with totals */}
            <TableFooter>
              <TableRow className="bg-gray-100">
                <TableCell className="font-bold">TOTAL</TableCell>
                <TableCell className="text-right">-</TableCell>
                <TableCell className="text-right font-bold">
                  {Object.values(futureForecastData.totalByMonth || {}).reduce((sum, val) => sum + val, 0).toLocaleString()}
                </TableCell>
                {Object.keys(futureForecastData.categories || {})
                  .filter(category => {
                    // Filter out categories with all zero values
                    const hasNonZeroValues = Object.values(futureForecastData.categories[category] || {})
                      .some(value => value > 0);
                    return hasNonZeroValues;
                  })
                  .map(category => {
                    const categoryTotal = Object.values(futureForecastData.categories[category] || {}).reduce((sum, val) => sum + val, 0);
                    return (
                      <TableCell key={`total-${category}`} className="text-right font-bold">
                        {categoryTotal.toLocaleString()}
                      </TableCell>
                    );
                  })}
              </TableRow>
            </TableFooter>
          </Table>
        </div>
        
        {/* Note section */}
        <div className="mt-4 text-sm text-gray-500">
          <p>Note: This table shows the predicted budget forecast for each month and category based on current growth rates. Current stock is shown for comparison.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default BudgetForecastTable; 
